import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SliceMetric, SliceDimension } from '@death-star/shared';
import { FilterBar } from '../components/slicing/FilterBar.js';
import { MetricHeader, type SliceStatus } from '../components/slicing/MetricHeader.js';
import { SliceTile } from '../components/slicing/SliceTile.js';
import { MetricTabs } from '../components/slicing/MetricTabs.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { useApiQuery } from '../use-api.js';
import { useAnnotations } from '../lib/use-annotations.js';

export type WindowKey = '7d' | '30d' | '90d' | 'since-2026-01-01';

// Empty string = "All orgs" — matches the sentinel weekly-digest and
// velocity-scorecard use. The API treats a missing `org` param the same way.
const ALL_ORGS = '';

const DIMENSIONS: readonly SliceDimension[] = [
  'size_bucket', 'path_category', 'change_type', 'author_type', 'primary_language',
];

interface ApiResponse {
  metric: SliceMetric;
  summary: { current: number; baseline: number | null; delta: number | null; target: number; status: SliceStatus; sample_n: number };
  breakdowns: Partial<Record<SliceDimension, { buckets: Array<{ name: string; current: number; baseline: number | null; n: number }>; note?: string }>>;
  trend: { dates: string[]; series: Partial<Record<SliceDimension, Record<string, number[]>>> };
}


export function Slicing() {
  const [search, setSearch] = useSearchParams();

  const { data: reposData } = useApiQuery<Array<{ org: string; repo: string }>>(
    ['repos-for-slicing-filter'],
    '/api/repos',
  );

  // Group /api/repos result into the shape FilterBar needs.
  // Memoized so the FilterBar reference is stable across renders.
  const { knownOrgs, reposByOrg } = useMemo(() => {
    const byOrg: Record<string, string[]> = {};
    for (const { org, repo } of reposData ?? []) {
      if (!byOrg[org]) byOrg[org] = [];
      byOrg[org]!.push(repo);
    }
    for (const repos of Object.values(byOrg)) repos.sort();
    return {
      knownOrgs: Object.keys(byOrg).sort(),
      reposByOrg: byOrg,
    };
  }, [reposData]);

  // Treat unknown ?org= values (e.g. someone hand-edits the URL or a stale
  // bookmark) as if the param were absent. Otherwise the controlled <select>
  // has no matching <option>, the Repo picker is empty, and we'd issue a
  // request that 400s with "unknown org". Mirrors weekly-digest's isValidOrg.
  const rawOrgParam = search.get('org');
  const org: string = rawOrgParam && knownOrgs.includes(rawOrgParam) ? rawOrgParam : ALL_ORGS;
  const isAllOrgs = org === ALL_ORGS;
  const repoParam = search.getAll('repo');
  const repos: string[] = isAllOrgs
    ? []
    : repoParam.length === 0 ? (reposByOrg[org] ?? []) : repoParam;
  const window = (search.get('window') ?? '30d') as WindowKey;
  const metric = (search.get('metric') ?? 'auto_approval') as SliceMetric;

  const updateParam = (key: string, value: string | string[] | null) => {
    const next = new URLSearchParams(search);
    if (value === null) {
      next.delete(key);
    } else if (Array.isArray(value)) {
      next.delete(key);
      for (const v of value) next.append(key, v);
    } else {
      next.set(key, value);
    }
    setSearch(next);
  };

  const queryString = (() => {
    const p = new URLSearchParams();
    if (!isAllOrgs) {
      p.set('org', org);
      for (const r of repos) p.append('repo', r);
    }
    p.set('window', window);
    p.set('metric', metric);
    return p.toString();
  })();

  // Defer the slicing fetch until /api/repos resolves. Without this, a deep
  // link like `/slicing?org=rewindio` fires an all-orgs request first (because
  // `knownOrgs` is `[]` while reposData is undefined, so the unknown-org guard
  // on line 57 collapses `org` to ALL_ORGS), then re-fires single-org once
  // reposData lands — doubling the expensive all-orgs path.
  const { data, isLoading, error } = useApiQuery<ApiResponse>(
    ['slicing', org, repos.join(','), window, metric],
    `/api/slicing?${queryString}`,
    { enabled: reposData !== undefined },
  );
  // Keyed off the dates the trend actually plots, not the window key, so the
  // markers can never be fetched for a different range than the lines.
  const trendDates = data?.trend.dates ?? [];
  const annotations = useAnnotations(trendDates[0], trendDates[trendDates.length - 1]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>
          Slicing dashboard
          {reposData === undefined && (
            <span style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 400, color: 'var(--loop-neutral-700)' }}>
              (loading repos…)
            </span>
          )}
        </h2>
        <ConceptHelpIcon slug="slicing" />
      </div>

      <FilterBar
        org={org}
        orgs={knownOrgs}
        repos={isAllOrgs ? [] : (reposByOrg[org] ?? [])}
        selectedRepos={repos}
        window={window}
        metric={metric}
        onOrgChange={(o) => {
          const next = new URLSearchParams(search);
          if (o === ALL_ORGS) {
            next.delete('org');
          } else {
            next.set('org', o);
          }
          next.delete('repo');
          setSearch(next);
        }}
        onReposChange={(rs) => updateParam('repo', rs)}
        onWindowChange={(w) => updateParam('window', w)}
      />

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error ? <p className="error">Error: {String(error)}</p> : null}

      {data && (
        <div className="card" style={{ padding: 18 }}>
          <MetricTabs active={data.metric} onSelect={(m) => updateParam('metric', m)} />

          <MetricHeader
            metric={data.metric}
            current={data.summary.current}
            baseline={data.summary.baseline}
            delta={data.summary.delta}
            sampleN={data.summary.sample_n}
            status={data.summary.status}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {DIMENSIONS.map((dim) => {
              const b = data.breakdowns[dim];
              const trendSeries = data.trend.series[dim] ?? {};
              return (
                <SliceTile
                  key={dim}
                  dimension={dim}
                  metric={data.metric}
                  buckets={b?.buckets ?? []}
                  trendDates={data.trend.dates}
                  trendSeries={trendSeries}
                  note={b?.note}
                  annotations={annotations}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
